/**
 * Prose — long-form markdown content (the backbone of the About / Lingua Franca
 * essay and legal pages). Dependency-free: a lightweight markdown→HTML converter
 * (headings, bold, italic, links, inline code, lists, paragraphs) rendered with
 * token-styled child selectors. No bespoke stylesheet — every style maps to a
 * design token via Tailwind utilities.
 */
import { cn } from '../../utils';

export interface ProseProps {
  readonly content: string;
  readonly className?: string;
}

/** Apply inline formatting: bold, italic, links, inline code. Exported so other
 *  primitives (e.g. ProseSection margin decks) render the same lightweight
 *  markdown without re-implementing it. */
export function inlineFormat(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Lightweight markdown→HTML: fenced code, headings, lists, paragraphs + inline. */
/** Split a `|a|b|c|` row into trimmed cells, honouring `\|` escapes inside cells. */
const tableCells = (l: string): string[] =>
  l.trim().replace(/^\||\|$/g, '').split(/(?<!\\)\|/).map((c) => c.trim().replace(/\\\|/g, '|'));
const isTableRow = (l: string): boolean => /^\s*\|.*\|\s*$/.test(l);
const isTableSep = (l: string): boolean => /^\s*\|[\s:|-]+\|\s*$/.test(l) && l.includes('-');

function markdownToHtml(md: string): string {
  const lines = md.split('\n');
  const out: string[] = [];
  // Heading-level normalization. The page hero owns <h1>, so the shallowest
  // heading in this content must render as <h2> and descend contiguously from
  // there — no skipped levels (WCAG/Lighthouse heading order). Source docs vary
  // (a sliced doc section may start at ## or ###), so shift every heading by the
  // minimum present. Prose never emits <h1>; levels clamp to [2,6].
  let minLevel = 7;
  let scanFence = false;
  for (const l of lines) {
    if (l.trimStart().startsWith('```')) { scanFence = !scanFence; continue; }
    if (scanFence) continue; // a `# comment` inside a code fence is not a heading
    const m = l.match(/^(#{1,6})\s+\S/);
    if (m) minLevel = Math.min(minLevel, m[1]!.length);
  }
  const headingShift = minLevel <= 6 ? 2 - minLevel : 0;
  // null = no open list; otherwise the open list's tag. Tracking the type lets a
  // bulleted (`-`) and a numbered (`1.`) list close/reopen correctly, so ordered
  // lists render as <ol> (numbered) rather than literal "1." paragraphs.
  let listType: 'ul' | 'ol' | null = null;
  let inCode = false;
  const codeBuf: string[] = [];
  const closeList = () => {
    if (listType) { out.push(`</${listType}>`); listType = null; }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? '';

    // Fenced code blocks (``` … ```)
    if (line.trimStart().startsWith('```')) {
      if (inCode) {
        out.push(`<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`);
        codeBuf.length = 0;
        inCode = false;
      } else {
        closeList();
        inCode = true;
      }
      continue;
    }
    if (inCode) { codeBuf.push(line); continue; }

    if (line.trim() === '') { closeList(); continue; }

    // Pipe tables: a `|h|h|` header row immediately followed by a `|---|---|`
    // separator, then `|c|c|` body rows. (Used e.g. by the generated API reference.)
    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1]!)) {
      closeList();
      const head = tableCells(line);
      const rows: string[][] = [];
      let j = i + 2;
      while (j < lines.length && isTableRow(lines[j]!)) { rows.push(tableCells(lines[j]!)); j++; }
      const thead = `<thead><tr>${head.map((c) => `<th>${inlineFormat(c)}</th>`).join('')}</tr></thead>`;
      const tbody = `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${inlineFormat(c)}</td>`).join('')}</tr>`).join('')}</tbody>`;
      out.push(`<table>${thead}${tbody}</table>`);
      i = j - 1;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      closeList();
      const level = Math.min(6, Math.max(2, heading[1]!.length + headingShift));
      out.push(`<h${level}>${inlineFormat(heading[2]!)}</h${level}>`);
      continue;
    }

    const bullet = line.match(/^[-*+]\s+(.+)$/);
    const numbered = line.match(/^\d+\.\s+(.+)$/);
    if (bullet || numbered) {
      const type = bullet ? 'ul' : 'ol';
      if (listType !== type) { closeList(); out.push(`<${type}>`); listType = type; }
      out.push(`<li>${inlineFormat((bullet ?? numbered)![1]!)}</li>`);
      continue;
    }

    closeList();
    out.push(`<p>${inlineFormat(line)}</p>`);
  }
  if (inCode) out.push(`<pre><code>${escapeHtml(codeBuf.join('\n'))}</code></pre>`);
  closeList();
  return out.join('\n');
}

const PROSE_CLASSES = cn(
  // Prose spans its container's full width (in a ProseSection card it fills the
  // card; figures float and text wraps as a magazine layout). Non-card/banded
  // prose is constrained to the reading measure by laned() in MarketingPage,
  // not here.
  'max-w-none text-foreground',
  '[&>p]:my-4 [&>p]:leading-relaxed [&>p]:text-muted-foreground',
  '[&>h2]:mt-12 [&>h2]:mb-4 [&>h2]:font-display [&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:text-foreground',
  '[&>h3]:mt-8 [&>h3]:mb-3 [&>h3]:font-display [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:text-foreground',
  '[&>h4]:mt-6 [&>h4]:mb-2 [&>h4]:font-semibold [&>h4]:text-foreground',
  '[&>ul]:my-5 [&>ul]:space-y-2 [&>ul]:pl-5 [&>ul]:list-disc [&>ul]:marker:text-primary [&>ul>li]:pl-1.5 [&>ul>li]:leading-relaxed [&>ul>li]:text-muted-foreground',
  '[&>ol]:my-5 [&>ol]:space-y-2 [&>ol]:pl-5 [&>ol]:list-decimal [&>ol]:marker:text-primary [&>ol>li]:pl-1.5 [&>ol>li]:text-muted-foreground',
  '[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:text-primary-hover',
  '[&_strong]:font-semibold [&_strong]:text-foreground',
  // Editorial italic emphasis — Instrument Serif, accent-coloured (matches the deck).
  '[&_em]:font-editorial [&_em]:font-normal [&_em]:text-[1.05em] [&_em]:text-primary',
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-sm',
  // Code blocks wrap rather than scroll sideways — preserve indentation (pre-wrap)
  // but break long lines/tokens so nothing runs off the edge.
  '[&>pre]:my-6 [&>pre]:whitespace-pre-wrap [&>pre]:break-words [&>pre]:rounded-card [&>pre]:border [&>pre]:border-border [&>pre]:bg-muted [&>pre]:p-4',
  '[&>pre>code]:bg-transparent [&>pre>code]:p-0 [&>pre>code]:text-sm [&>pre>code]:leading-relaxed',
  // Pipe tables (e.g. the generated API reference) — fill the available width and
  // wrap cell content; no sideways scroll. Mark the block `wide` for the full page.
  '[&_table]:my-6 [&_table]:w-full [&_table]:table-auto [&_table]:border-collapse [&_table]:text-sm',
  '[&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:text-foreground',
  '[&_td]:border [&_td]:border-border [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_td]:text-muted-foreground [&_td]:break-words',
);

export function Prose({ content, className }: ProseProps) {
  return (
    <div
      className={cn(PROSE_CLASSES, className)}
      dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }}
    />
  );
}
